import { createSelector } from '@reduxjs/toolkit';
import { RootState } from 'MyTypes';
import { uniq } from 'lodash/fp';

import {
    getAiChatFeedbackCategoryLocaleKey,
    mapReactionValueToLabel,
} from '@portal/common/models/ai-chat-feedback';
import {
    AGE_GROUP_ID_KEY,
    ANTIBIOTIC_CLASS_ID_KEY,
    APPARENT_ID_KEY,
    CAUSE_ID_KEY,
    COUNTERFACTUAL_ID_KEY,
    COVARIATE_ID_KEY,
    CRO_ID_KEY,
    DATE_KEY,
    EDUCATION_ID_KEY,
    ETHNICITY_ID_KEY,
    FACILITY_ID_KEY,
    FORECAST_SCENARIO_ID_KEY,
    GENDER_ID_KEY,
    INDICATOR_ID_KEY,
    INDUSTRY_ID_KEY,
    INFECTION_TYPE_ID_KEY,
    INFECTIOUS_SYNDROME_ID_KEY,
    LOCATION_ID_KEY,
    MEASURE_ID_KEY,
    METRIC_ID_KEY,
    ORGANIZATION_ID_KEY,
    OUTCOME_ID_KEY,
    PATHOGEN_ID_KEY,
    PAYER_ID_KEY,
    RACE_ID_KEY,
    REI_ID_KEY,
    RESOURCE_ID_KEY,
    RESPONSE_ID_KEY,
    RISK_EXPOSURE_ID_KEY,
    ROUND_ID_KEY,
    SDOH_ID_KEY,
    SEQUELA_ID_KEY,
    SEVERITY_ID_KEY,
    SYMPTOMATIC_ID_KEY,
    TOC_ID_KEY,
    VACCINATION_STATUS_ID_KEY,
    VALUE_KEY,
    VEHSS_CONDITION_ID_KEY,
} from '@portal/common/models/data-key';
import { DataGranularityKey, DataKey, Dataset } from '@portal/common/types';
import {
    formatValue,
    getLocalePrefixForGranularityKey,
} from '@portal/common/utility/filters-helpers';

import _ from '../../locale';
import {
    DELIVERY_METHOD_KEY,
    DeliveryMethod,
    EventType,
    ReportingParam,
    VisualizationConfig,
    conditionSearchReportingParams,
    dataExportStandardReportingParams,
    fileDownloadReportingParams,
    pageViewReportingParams,
    pdfExportStandardReportingParams,
    standardReportingParams,
    userFeedbackReportingParams,
} from './models';

export const getInsightsEventType = (state: RootState) => state.insights.eventType;

export const getInsightsDataResponse = (state: RootState) => state.insights.insightsDataResponse;
export const getInsightsCompanyUsageDataResponse = (state: RootState) =>
    state.insights[EventType.companyUsage].dataResponses;
export const isInsightsCompanyUsageLoading = (state: RootState) =>
    state.insights[EventType.companyUsage].isLoading;
export const getInsightsFromDate = (state: RootState) => state.insights.fromDate;
export const getInsightsToDate = (state: RootState) => state.insights.toDate;
export const getInsightsOrganizations = (state: RootState) => state.insights.organizations;
export const getInsightsVisualizations = (state: RootState): VisualizationConfig[] =>
    state.insights[EventType.companyUsage].visualizations;

export const getInsightsVisualizationOptions = (state: RootState): VisualizationConfig[] =>
    state.insights[EventType.companyUsage].visualizationOptions;

export const getInsightsSelectedFilters = createSelector(
    (state: RootState) => state.insights,
    (insights) => {
        const { eventType, fromDate, toDate, organizations } = insights;
        return {
            ...insights[eventType],
            fromDate,
            toDate,
            organizations,
        };
    }
);

export const getInsightsDataProjectId = createSelector(
    getInsightsSelectedFilters,
    (filters) => filters.dataProjectId
);

export const getInsightsDataTableColumns = (
    eventType: EventType,
    datasets?: Dataset[] | null,
    dataProjectId?: number[]
): readonly ReportingParam[] => {
    return getInsightsReportingParams(eventType, datasets, dataProjectId);
};

export const getInsightsGroupByParams = (
    eventType: EventType,
    datasets?: Dataset[] | null,
    dataProjectId?: number[]
): readonly ReportingParam[] =>
    getInsightsReportingParams(eventType, datasets, dataProjectId).filter(
        (param) => param !== DELIVERY_METHOD_KEY
    );

export const getInsightsReportingParams = (
    eventType: EventType,
    datasets?: Dataset[] | null,
    dataProjectId?: number[]
): readonly ReportingParam[] => {
    const standardParams = standardReportingParams as readonly ReportingParam[];

    switch (eventType) {
        case EventType.visit:
        case EventType.dataQuery:
        case EventType.fileDownload:
        case EventType.pageView:
        case EventType.dataExport:
        case EventType.pdfExport:
        case EventType.userFeedback:
        case EventType.aiChat:
        case EventType.aiFeedback:
        case EventType.conditionSearch:
        case EventType.companyUsage:
            return standardParams.concat(
                getCustomReportingParams(eventType, datasets, dataProjectId)
            );

        default:
            throw Error('Incorrect event type');
    }
};

export const getDatasetsGranularityKeys = (
    datasets?: Dataset[] | null,
    dataProjectId?: number[]
): DataGranularityKey[] => {
    if (!(datasets && dataProjectId)) {
        return [];
    }

    const dataProjectDatasets = datasets.filter(({ data_project_id }) =>
        dataProjectId.includes(data_project_id)
    );
    return uniq(
        dataProjectDatasets.map(({ granularity }) => Object.keys(granularity)).flat()
    ) as DataGranularityKey[];
};

export const getCustomReportingParams = (
    eventType: EventType,
    datasets?: Dataset[] | null,
    dataProjectId?: number[]
): readonly ReportingParam[] => {
    switch (eventType) {
        case EventType.visit:
            return [];
        case EventType.dataQuery:
            return getDataQueryReportingParams(dataProjectId, datasets);
        case EventType.fileDownload:
            return fileDownloadReportingParams;
        case EventType.pageView:
            return pageViewReportingParams;
        case EventType.dataExport:
            return getDataExportReportingParams(dataProjectId, datasets);
        case EventType.pdfExport:
            return getPdfExportReportingParams(dataProjectId, datasets);
        case EventType.userFeedback:
            return userFeedbackReportingParams;
        case EventType.aiChat:
        case EventType.aiFeedback:
            return [];
        case EventType.conditionSearch:
            return conditionSearchReportingParams;
        case EventType.companyUsage:
            return [];
        default:
            throw Error('Incorrect event type');
    }
};

const getDataQueryReportingParams = (
    dataProjectId?: number[],
    datasets?: Dataset[] | null
): ReportingParam[] =>
    ['resource_id', ...getDatasetsGranularityKeys(datasets, dataProjectId)] as ReportingParam[];
const getDataExportReportingParams = (
    dataProjectId?: number[],
    datasets?: Dataset[] | null
): ReportingParam[] => [
    ...dataExportStandardReportingParams,
    ...getDatasetsGranularityKeys(datasets, dataProjectId),
];
const getPdfExportReportingParams = (
    dataProjectId?: number[],
    datasets: Dataset[]
): ReportingParam[] => [
    ...pdfExportStandardReportingParams,
    ...getDatasetsGranularityKeys(datasets, dataProjectId),
];

export const getLocalizedInsightsDataResponse = createSelector(
    getInsightsDataResponse,
    (response) => {
        if (response == null) {
            return null;
        }

        const columns = getLocalizedColumns(response.columns);
        const records = getLocalizedRecords(response);

        return { columns, records };
    }
);

export const getLocalizedColumns = (
    columns: DataKey[],
    includeIds?: boolean,
    replaceSpacesInColumnNames?: boolean
) => {
    const NON_PARAM_COLUMNS = ['value', 'date'];

    const columnsCopy = [...columns];

    if (includeIds) {
        columns
            .filter((key) => key.includes('_id'))
            .forEach((key) => {
                columnsCopy.push(key.replace('_id', ''));
            });
    }

    const result = columnsCopy.map((columnKey) => {
        const prefix = NON_PARAM_COLUMNS.includes(columnKey)
            ? 'insights_columns_'
            : 'analytics_param_';

        return formatValue(prefix, columnKey, _, {});
    });

    if (!replaceSpacesInColumnNames) {
        return result;
    }

    return result.map((col) => col.replace(/\s+/g, '_'));
};

export const getLocalizedRecords = (response, includeIds = false) => {
    const { columns } = response;
    const records = response.records || response.results;

    return records.map((record) => {
        const recordCopy = [...record];

        if (includeIds) {
            record.forEach((value, idx) => {
                if (columns[idx].includes('_id')) {
                    recordCopy.push(value);
                }
            });
        }

        return recordCopy.map((value, idx) => localizeRecordValue(value, columns[idx]));
    });
};

export const localizeRecordValue = (value, columnKey) => {
    const FILTER_COLUMNS = [
        ROUND_ID_KEY,
        CAUSE_ID_KEY,
        CRO_ID_KEY,
        SEQUELA_ID_KEY,
        REI_ID_KEY,
        RISK_EXPOSURE_ID_KEY,
        COVARIATE_ID_KEY,
        SDOH_ID_KEY,
        LOCATION_ID_KEY,
        AGE_GROUP_ID_KEY,
        GENDER_ID_KEY,
        MEASURE_ID_KEY,
        METRIC_ID_KEY,
        FORECAST_SCENARIO_ID_KEY,
        INFECTIOUS_SYNDROME_ID_KEY,
        ANTIBIOTIC_CLASS_ID_KEY,
        COUNTERFACTUAL_ID_KEY,
        PATHOGEN_ID_KEY,
        INDICATOR_ID_KEY,
        RACE_ID_KEY,
        FACILITY_ID_KEY,
        INDUSTRY_ID_KEY,
        RESPONSE_ID_KEY,
        ETHNICITY_ID_KEY,
        EDUCATION_ID_KEY,
        ORGANIZATION_ID_KEY,
        RESOURCE_ID_KEY,
        VEHSS_CONDITION_ID_KEY,
        PAYER_ID_KEY,
        TOC_ID_KEY,
        APPARENT_ID_KEY,
        INFECTION_TYPE_ID_KEY,
        VACCINATION_STATUS_ID_KEY,
        OUTCOME_ID_KEY,
        SYMPTOMATIC_ID_KEY,
        SEVERITY_ID_KEY,
    ];
    const NUMERIC_COLUMNS = [VALUE_KEY];
    const NON_LOCALIZE_COLUMNS = [DATE_KEY];
    const BOOLEAN_COLUMNS = ['is_new_account'];

    if (NUMERIC_COLUMNS.includes(columnKey)) {
        return Number(value);
    }

    if (BOOLEAN_COLUMNS.includes(columnKey)) {
        return Number(value) ? _('yes_label') : _('no_label');
    }

    if (columnKey === 'reaction') {
        return mapReactionValueToLabel(value);
    }

    if (columnKey === 'categories' && value) {
        return String(value)
            .split(', ')
            .map((category) => {
                const key = getAiChatFeedbackCategoryLocaleKey(category);
                const translated = _(key);

                return translated !== key ? translated : category;
            })
            .join(', ');
    }

    if (NON_LOCALIZE_COLUMNS.includes(columnKey)) {
        return value;
    }

    let prefix = '';
    if (FILTER_COLUMNS.includes(columnKey)) {
        prefix = columnKey;
    } else {
        switch (columnKey) {
            case 'visible_filters':
                prefix = 'refinement_';
                break;
            case 'queried_data_types':
                prefix = 'data_type_';
                break;
            case 'source':
                prefix = 'source_';
                break;
            case 'chart_types':
                prefix = 'chart_type_';
                break;
            case 'format':
                prefix = 'format_';
                break;
            case DELIVERY_METHOD_KEY:
                prefix = 'delivery_method_';
                break;
            case 'status':
                prefix = 'status_';
                break;
            case 'reason':
                prefix = 'reason_';
                break;
            case 'pdf_report_options':
                prefix = 'pdf_report_option_';
                value = Array.isArray(value)
                    ? value.map((_value) => _value.replace('-', '_'))
                    : value.replace('-', '_');
                break;
            default:
                break;
        }
    }

    let translation = formatValue(prefix, value, _);

    if (
        columnKey === RESOURCE_ID_KEY &&
        (translation || '').toString().startsWith(getLocalePrefixForGranularityKey(columnKey))
    ) {
        translation = _('removed_data_collection');
    }

    return translation;
};

export const getLocalizedInsightsGroupBy = createSelector(getInsightsSelectedFilters, (filters) =>
    getLocalizedColumns(filters.groupBy)
);

export const getInsightsGroupBy = createSelector(
    getInsightsSelectedFilters,
    (filters) => filters.groupBy
);

export const getInsightsDeliveryMethod = createSelector(
    getInsightsSelectedFilters,
    (filters) => filters.deliveryMethod || DeliveryMethod.download
);
